import { useAssignCompany } from "@/api/console/assessor/useAssignCompany";
import MultiSelect from "@/components/MultiSelect";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import toast from "@/lib/toast";
import {
  AssignCompany,
  assignCompanySchema,
} from "@/schema/console/assessors/AssignCompany";
import useAssessorDialog from "@/src/console/(auth)/assessors/_hooks/useAssessorDialog";
import useGetCompanyOptions from "@/src/console/(auth)/assessors/_hooks/useGetCompanyOptiions";
import { SpinnerIosRegular } from "@fluentui/react-icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { useController, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { mutate } from "swr";

function AssignCompanyDialog() {
  const { t } = useTranslation();
  const { type, assessor, resetDialog } = useAssessorDialog();
  const assessorId = assessor?.id ?? -1;

  const { companyOptions } = useGetCompanyOptions();

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<AssignCompany>({
    resolver: zodResolver(assignCompanySchema),
    values: { company_ids: assessor?.companies.map(({ id }) => id) ?? [] },
  });

  const {
    field: { value, onChange },
  } = useController({
    control,
    name: "company_ids",
  });

  const { trigger, isMutating } = useAssignCompany(assessorId, {
    onSuccess: (data) => {
      toast.success(data.message);
      mutate("/console/admin_users");
      resetDialog();
    },
  });

  return (
    <Dialog
      open={type === "assign-company"}
      onOpenChange={(open) => !open && resetDialog()}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("assign-company")}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            handleSubmit((data) => {
              trigger(data);
            })();
          }}
        >
          <div className="flex flex-col gap-6 px-6 pb-5 pt-4">
            <MultiSelect
              value={value}
              onChange={(value) => onChange(value)}
              placeholder={t("select-company")}
              data={companyOptions}
            />
            <div>
              {value.map((company_id) => (
                <div key={company_id}>
                  {
                    companyOptions.find(
                      (company) => company.value === company_id,
                    )?.label
                  }
                </div>
              ))}
            </div>
            <Button
              isLoading={isMutating}
              LoadingComponent={<SpinnerIosRegular className="animate-spin" />}
              type="submit"
            >
              {t("assign-company")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default AssignCompanyDialog;
